import { listPersonas } from '@baduk/bots';
import { SUPPORTED_SIZES } from '@baduk/engine';

export interface GameSetup {
  /** Play against a bot, or sit out and watch two of them. */
  readonly mode: 'play' | 'watch';
  /** Your opponent when playing; Black when watching. */
  readonly botId: string;
  /** White when watching. Ignored when playing. */
  readonly whiteBotId: string;
  readonly size: number;
  readonly handicap: number;
  readonly humanIsBlack: boolean;
  readonly thinkMs: number;
  /** Pause between moves when watching, so a game of instant bots is followable. */
  readonly moveDelayMs: number;
}

export interface GamePanelProps {
  /** What the panel is editing — not necessarily what the board is playing. */
  readonly draft: GameSetup;
  onChange(draft: GameSetup): void;
  /** The settings the game in progress was actually started with. */
  readonly active: GameSetup;
  onStart(): void;
  readonly open: boolean;
  onOpenChange(open: boolean): void;
  readonly moveCount: number;
}

/** Field-by-field, so the panel can say exactly what a new game would change. */
export function pendingChanges(draft: GameSetup, active: GameSetup): string[] {
  const differences: string[] = [];
  if (draft.mode !== active.mode) differences.push(draft.mode === 'watch' ? 'watching' : 'playing');
  if (draft.size !== active.size) differences.push(`${draft.size}×${draft.size}`);
  if (draft.botId !== active.botId || (draft.mode === 'watch' && draft.whiteBotId !== active.whiteBotId)) {
    differences.push('opponent');
  }
  if (draft.handicap !== active.handicap) differences.push('handicap');
  if (draft.humanIsBlack !== active.humanIsBlack) differences.push('colour');
  return differences;
}

/**
 * Everything about the game being played, in the rail beside the board.
 *
 * This used to be a full screen you passed through once and never saw again,
 * which made changing opponent a matter of abandoning the game and starting
 * over. Here it is simply another panel: the board is up and playable from the
 * first moment, and the settings stay to hand.
 *
 * Nothing here takes effect until a new game is started. Editing these while a
 * game is in progress would otherwise swap a player's opponent mid-fight, or
 * hand both colours to bots halfway through — so the panel edits a draft and
 * says what starting again would change.
 */
export function GamePanel({
  draft,
  onChange,
  active,
  onStart,
  open,
  onOpenChange,
  moveCount,
}: GamePanelProps) {
  const personas = listPersonas();
  const watching = draft.mode === 'watch';
  const chosen = personas.find((p) => p.id === draft.botId) ?? personas[0];
  const pending = pendingChanges(draft, active);
  const started = moveCount > 0;

  return (
    <details
      className="game-panel"
      open={open}
      onToggle={(event) => onOpenChange((event.currentTarget as HTMLDetailsElement).open)}
    >
      <summary>
        <span className="game-title">Game</span>
        <span className="muted small">
          {active.mode === 'watch'
            ? 'bot vs bot'
            : `vs ${personas.find((p) => p.id === active.botId)?.name ?? 'bot'}`}
        </span>
      </summary>

      <div className="mode-switch" role="group" aria-label="Play or watch">
        {(
          [
            ['play', 'Play'],
            ['watch', 'Watch'],
          ] as const
        ).map(([mode, label]) => (
          <button
            key={mode}
            type="button"
            className={`mode-option${draft.mode === mode ? ' selected' : ''}`}
            onClick={() => onChange({ ...draft, mode })}
            aria-pressed={draft.mode === mode}
          >
            {label}
          </button>
        ))}
      </div>

      {watching ? (
        <div className="setup-options">
          <label>
            <span>Black</span>
            <select
              value={draft.botId}
              onChange={(event) => onChange({ ...draft, botId: event.target.value })}
            >
              {personas.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.avatar} {p.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>White</span>
            <select
              value={draft.whiteBotId}
              onChange={(event) => onChange({ ...draft, whiteBotId: event.target.value })}
            >
              {personas.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.avatar} {p.name}
                </option>
              ))}
            </select>
          </label>
        </div>
      ) : (
        <>
          {/*
            Compact rows rather than the old cards. Only the chosen opponent
            explains itself below — four descriptions at once would fill the rail
            and none of them would be read.
          */}
          <ul className="persona-rows">
            {personas.map((persona) => (
              <li key={persona.id}>
                <button
                  type="button"
                  className={`persona-row${persona.id === draft.botId ? ' selected' : ''}`}
                  style={{ '--accent': persona.accent } as React.CSSProperties}
                  onClick={() => onChange({ ...draft, botId: persona.id })}
                  aria-pressed={persona.id === draft.botId}
                >
                  <span aria-hidden="true">{persona.avatar}</span>
                  <strong>{persona.name}</strong>
                  <span className="muted small">{persona.rank}</span>
                </button>
              </li>
            ))}
          </ul>

          <p className="persona-tagline">{chosen.tagline}</p>
          <p className="persona-detail">
            <strong>Teaches</strong> {chosen.teaches}
          </p>
          <p className="persona-detail muted">
            <strong>Weak at</strong> {chosen.weakness}
          </p>
        </>
      )}

      <div className="setup-options">
        <label>
          <span>Board</span>
          <select
            value={draft.size}
            onChange={(event) => onChange({ ...draft, size: Number(event.target.value) })}
          >
            {SUPPORTED_SIZES.map((size) => (
              <option key={size} value={size}>
                {size}×{size}
              </option>
            ))}
          </select>
        </label>

        {!watching && (
          <>
            <label>
              <span>Handicap</span>
              <select
                value={draft.handicap}
                onChange={(event) => onChange({ ...draft, handicap: Number(event.target.value) })}
              >
                <option value={0}>None</option>
                {[2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                  <option key={n} value={n}>
                    {n} stones
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>You play</span>
              <select
                value={draft.humanIsBlack ? 'black' : 'white'}
                onChange={(event) =>
                  onChange({ ...draft, humanIsBlack: event.target.value === 'black' })
                }
              >
                <option value="black">Black</option>
                <option value="white">White</option>
              </select>
            </label>
          </>
        )}

        <label>
          <span>Thinking time</span>
          <select
            value={draft.thinkMs}
            onChange={(event) => onChange({ ...draft, thinkMs: Number(event.target.value) })}
          >
            <option value={400}>Fast</option>
            <option value={1200}>Normal</option>
            <option value={3000}>Careful</option>
          </select>
        </label>

        {watching && (
          <label>
            <span>Pace</span>
            <select
              value={draft.moveDelayMs}
              onChange={(event) => onChange({ ...draft, moveDelayMs: Number(event.target.value) })}
            >
              <option value={0}>Fast as they think</option>
              <option value={600}>0.6s a move</option>
              <option value={1500}>1.5s a move</option>
            </select>
          </label>
        )}
      </div>

      <button type="button" className="primary start" onClick={onStart}>
        {pending.length > 0 ? 'Start new game' : started ? 'Restart' : 'New game'}
      </button>

      {pending.length > 0 && (
        <p className="muted small pending-note">
          {started ? 'The game in progress will be abandoned. ' : ''}
          Changing {pending.join(', ')} takes effect on a new game.
        </p>
      )}
    </details>
  );
}
